import { chain, find, isString } from "lodash";
import moment from "moment";
import { RefObject, useEffect, useState } from "react";
import { useLocalstorageState } from "rooks";
import GratefulDB, { Entry, Track } from "../../GratefulDB";
import AUD512 from "../../images/AUD-512.jpg";
import SBD512 from "../../images/SBD-512.jpg";
import { Constants } from "./Constants";
import { constructTrackUrl, entryVenueCoverage } from "./Helpers";
import { useNavUtils } from "./useNavUtils";
import { usePassword } from "./usePassword";

enum PlayerType {
  Radio = "RADIO",
}

enum PlayState {
  Hidden = "HIDDEN",
  Loading = "LOADING",
  Playing = "PLAYING",
  Paused = "PAUSED",
}

export interface AudioControls {
  stepCounter: number;
  selectTrack: (
    entry: Entry,
    track: Track,
    disableAutoplay: boolean,
    startSeek?: number,
    playerType?: PlayerType,
    metadata?: Metadata
  ) => void;
  startRadio: (startYear: number, endYear: number) => void;
  randomRadioEntryTrack: (
    startYear: number,
    endYear: number,
    previousTitleStubs: string[]
  ) => RandomEntry;
  selectCurrentShow: () => void;
  nextTrack: () => void;
  togglePlay: () => void;
  isPlaying: () => boolean;
  isVisible: () => boolean;
  isLoading: () => boolean;
  isRadio: () => boolean;
  play: () => void;
  pause: () => void;
  getDuration: () => number;
  getCurrentTime: () => number;
  setCurrentTime: (time: number) => void;
  isStep: (step: number) => boolean;
  getPlayerState: () => PlayerState;
  getEntry: () => Entry | undefined;
  getTrack: () => Track | undefined;
  maybePersistAudioState: () => void;
  checkPersistedAudioState: () => void;
  isActiveTrack: (entry: Entry, track: Track) => boolean;
  setMediaSession: () => void;
}

type RandomEntry = {
  entry: Entry;
  track: Track;
  stub: string;
};

type Metadata = {
  startYear: number;
  endYear: number;
  next: RandomEntry;
  previousTitleStubs: string[];
};

type PlayerState = {
  state: PlayState;
  entry?: Entry;
  track?: Track;
  playerType?: PlayerType;
  metadata?: Metadata;
};

type PersistedPlayerState = {
  entry: Entry;
  track: Track;
  currentTime: number;
  expires: moment.Moment;
};

const ArtworkSizes = [96, 128, 192, 256, 384, 512];

export const useAudioControls = (
  audio: RefObject<HTMLAudioElement>,
  gratefulDB: GratefulDB
): AudioControls => {
  const navUtils = useNavUtils();
  const password = usePassword();

  const [playerState, setPlayerState] = useState<PlayerState>({
    state: PlayState.Hidden,
  });
  const [persistedPlayerState, setPersistedPlayerState] =
    useLocalstorageState<PersistedPlayerState>("PersistedPlayerState");

  const audioControls: AudioControls = {
    stepCounter: 0,
    selectTrack: (
      entry,
      track,
      disableAutoplay,
      startSeek,
      playerType,
      metadata
    ) => {
      const state = {
        state: PlayState.Loading,
        entry,
        track,
        playerType,
        metadata,
      };
      setPlayerState(state);

      const setPlaying = () => {
        setPlayerState({
          ...state,
          state: PlayState.Playing,
        });
      };

      const setPaused = () => {
        setPlayerState({
          ...state,
          state: PlayState.Paused,
        });
      };

      if (audio.current) {
        const src = constructTrackUrl(entry, track, password.getHash());
        audio.current.src = src;
        audio.current.onplay = null;
        audio.current.onloadeddata = () => {
          if (!audio.current) {
            return;
          }
          if (!disableAutoplay) {
            setPlaying();
          } else {
            if (startSeek) {
              audio.current.currentTime = startSeek;
            }
            setPaused();
          }
          audio.current.onplay = setPlaying;
        };
        audio.current.onpause = setPaused;

        if (!disableAutoplay) {
          audio.current.play();
        } else {
          audio.current.load();
        }
      }
    },
    startRadio: (startYear, endYear) => {
      const start = audioControls.randomRadioEntryTrack(startYear, endYear, []);
      const next = audioControls.randomRadioEntryTrack(startYear, endYear, [
        start.stub,
      ]);
      audioControls.selectTrack(
        start.entry,
        start.track,
        false,
        undefined,
        PlayerType.Radio,
        {
          startYear,
          endYear,
          next,
          previousTitleStubs: [start.stub, next.stub],
        }
      );
    },
    randomRadioEntryTrack: (startYear, endYear, previousTitleStubs) => {
      const stub = (name: string) => {
        return name.replace(/\W+/g, "").toLowerCase();
      };
      const containsRestrictedWord = (titleStub: string) => {
        const restricted = ["tuning", "encore", "drums", "space", "drumsspace"];
        return (
          find(restricted, (word) => titleStub.toLowerCase().includes(word)) !=
          null
        );
      };

      const errorEntry = {
        identifier: "",
        title: "",
        date: "No Results Found",
        subject: "",
        venue: "Unknown",
        coverage: "Unknown",
        source: "",
        transferer: "",
        hosts: [],
        directory: "",
        isSoundboard: true,
        tracks: [],
      };

      const errorTrack = {
        name: "",
        format: "",
        length: "",
        title: "Error",
        track: "",
      };

      let entry;
      let track;
      let iterations = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (iterations > 1000) {
          return {
            entry: errorEntry,
            track: errorTrack,
            stub: "",
          };
        }
        entry = gratefulDB.randomRadioEntry(startYear, endYear);
        if (!entry || !entry.isSoundboard || !entry.tracks) {
          iterations++;
          continue;
        }
        track = chain(entry.tracks)
          .shuffle()
          .find(
            (track) =>
              !previousTitleStubs.includes(stub(track.title)) &&
              !containsRestrictedWord(stub(track.title))
          )
          .value();
        if (!track) {
          iterations++;
          continue;
        }
        break;
      }
      return {
        entry,
        track,
        stub: stub(track.title),
      };
    },
    selectCurrentShow: () => {
      if (!playerState.entry) {
        return;
      }
      navUtils.show(playerState.entry.identifier);
    },
    nextTrack: () => {
      if (!playerState.entry || !playerState.track) {
        return;
      }
      if (playerState.playerType === PlayerType.Radio) {
        if (!playerState.metadata) {
          return;
        }
        const next = audioControls.randomRadioEntryTrack(
          playerState.metadata.startYear,
          playerState.metadata.endYear,
          playerState.metadata.previousTitleStubs
        );
        const metadata = {
          ...playerState.metadata,
          next: next,
          previousTitleStubs: chain(playerState.metadata.previousTitleStubs)
            .unshift(next.stub)
            .take(60)
            .value(),
        };
        audioControls.selectTrack(
          playerState.metadata.next.entry,
          playerState.metadata.next.track,
          false,
          undefined,
          PlayerType.Radio,
          metadata
        );
        return;
      }
      const tracks = playerState.entry.tracks;
      const tracksLength = tracks.length;
      for (let i = 0; i < tracksLength; i++) {
        const track = tracks[i];
        if (
          track.track !== Constants.EmptyString &&
          track.track === playerState.track.track &&
          track.name === playerState.track.name &&
          i < tracksLength - 1
        ) {
          audioControls.selectTrack(playerState.entry, tracks[i + 1], false);
          break;
        }
      }
    },
    togglePlay: () => {
      if (audioControls.isPlaying()) {
        audioControls.pause();
      } else {
        audioControls.play();
      }
    },
    isPlaying: () => {
      return playerState.state === PlayState.Playing;
    },
    isVisible: () => {
      return playerState.state !== PlayState.Hidden;
    },
    isLoading: () => {
      return playerState.state === PlayState.Loading;
    },
    isRadio: () => {
      return playerState.playerType === PlayerType.Radio;
    },
    play: () => {
      if (!audio.current) {
        return;
      }
      audio.current.play();
    },
    pause: () => {
      if (!audio.current) {
        return;
      }
      audio.current.pause();
    },
    getDuration: () => {
      if (!audio.current) {
        return 0;
      }
      return audio.current.duration;
    },
    getCurrentTime: () => {
      if (!audio.current) {
        return 0;
      }
      return audio.current.currentTime;
    },
    setCurrentTime: (time) => {
      if (!audio.current) {
        return;
      }
      audio.current.currentTime = time;
    },
    isStep: (step) => {
      audioControls.stepCounter++;
      return audioControls.stepCounter % step === 0;
    },
    getPlayerState: () => {
      return playerState;
    },
    getEntry: () => {
      return playerState.entry;
    },
    getTrack: () => {
      return playerState.track;
    },
    maybePersistAudioState: () => {
      if (
        !audioControls.isStep(50) ||
        !audio.current ||
        !playerState.entry ||
        !playerState.track
      ) {
        return;
      }
      setPersistedPlayerState({
        entry: playerState.entry,
        track: playerState.track,
        currentTime: audio.current.currentTime,
        expires: moment().add(3, "days"),
      });
    },
    checkPersistedAudioState: () => {
      if (!persistedPlayerState || isString(persistedPlayerState)) {
        return;
      }
      const expires = moment(persistedPlayerState.expires);
      if (moment().isAfter(expires)) {
        return;
      }
      audioControls.selectTrack(
        persistedPlayerState.entry,
        persistedPlayerState.track,
        true,
        persistedPlayerState.currentTime
      );
    },
    isActiveTrack: (entry, track) => {
      if (!playerState.entry || !playerState.track) {
        return false;
      }
      return (
        playerState.entry &&
        playerState.track &&
        playerState.entry.identifier === entry.identifier &&
        playerState.track.track === track.track &&
        playerState.track.name === track.name
      );
    },
    setMediaSession: () => {
      if (!playerState.entry || !playerState.track) {
        return;
      }

      if ("mediaSession" in navigator) {
        const artwork = [];
        for (let i = 0; i < ArtworkSizes.length; i++) {
          const size = ArtworkSizes[i];
          artwork.push({
            src: playerState.entry.image
              ? playerState.entry.image.url
              : playerState.entry.isSoundboard
              ? SBD512
              : AUD512,
            sizes: `${size}x${size}`,
            type: "image/jpg",
          });
        }

        // eslint-disable-next-line no-undef
        navigator.mediaSession.metadata = new MediaMetadata({
          title: playerState.track.title,
          artist: playerState.entry.artist
            ? playerState.entry.artist
            : process.env.REACT_APP_ARTIST_NAME,
          album: `${playerState.entry.date} - ${entryVenueCoverage(
            playerState.entry
          )}`,
          artwork: artwork,
        });
        navigator.mediaSession.setActionHandler("play", audioControls.play);
        navigator.mediaSession.setActionHandler("pause", audioControls.pause);
        navigator.mediaSession.setActionHandler("seekto", (details) => {
          if (!audio.current || !details.seekTime) {
            return;
          }
          if (details.fastSeek && "fastSeek" in audio.current) {
            audio.current.fastSeek(details.seekTime);
            return;
          }
          audio.current.currentTime = details.seekTime;
        });
        navigator.mediaSession.setActionHandler(
          "nexttrack",
          audioControls.nextTrack
        );
      }
    },
  };

  useEffect(() => {
    if (audioControls.isPlaying()) {
      audioControls.setMediaSession();
    }
  }, [audioControls.getPlayerState()]);

  return audioControls;
};
